import { Injectable } from '@angular/core';

interface KeyPairData {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  private readonly KEY_STORE_NAME = 'MzicKeyStore';
  private readonly KEY_NAME = 'mzic_consent_key';
  private readonly PRIVATE_KEY_ID = `${this.KEY_NAME}_private`;
  private readonly PUBLIC_KEY_ID = `${this.KEY_NAME}_public`;

  // ECDSA P-256 (same as secp256r1)
  private readonly ALGORITHM: EcKeyGenParams = {
    name: 'ECDSA',
    namedCurve: 'P-256',
  };

  private readonly SIGNING_ALGORITHM: EcdsaParams = {
    name: 'ECDSA',
    hash: { name: 'SHA-256' },
  };

  /**
   * Check if Web Crypto API is available
   */
  isWebCryptoAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.crypto !== 'undefined' &&
      typeof window.crypto.subtle !== 'undefined'
    );
  }

  /**
   * Check if IndexedDB is available
   */
  isIndexedDBAvailable(): boolean {
    try {
      return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get existing key pair or create a new one
   */
  async getOrCreateKeyPair(): Promise<KeyPairData> {
    if (!this.isWebCryptoAvailable()) {
      throw new Error('Web Crypto API is not available in this browser');
    }

    try {
      const existingKeyPair = await this.loadKeyPairFromStorage();
      if (existingKeyPair) {
        return existingKeyPair;
      }
    } catch (error) {
      console.warn('Failed to load existing keys, will regenerate:', error);
      await this.clearKeys();
    }

    const keyPair = await this.generateECKeyPair();
    await this.saveKeyPairToStorage(keyPair);

    return keyPair;
  }

  /**
   * Generate ECDSA P-256 key pair using Web Crypto API
   */
  private async generateECKeyPair(): Promise<KeyPairData> {
    const keyPair = await window.crypto.subtle.generateKey(
      this.ALGORITHM,
      true, // extractable - needed for storage
      ['sign', 'verify'],
    );

    return {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
    };
  }

  /**
   * Sign data with ECDSA-SHA256 and convert to DER format
   *
   * @param data - The string data to sign
   * @param privateKey - The ECDSA private key
   * @returns Base64 encoded DER signature
   */
  async signData(data: string, privateKey: CryptoKey): Promise<string> {
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(data);

    const signatureBuffer = await window.crypto.subtle.sign(
      this.SIGNING_ALGORITHM,
      privateKey,
      dataBytes,
    );

    // Web Crypto produces IEEE P1363 format (r||s)
    // Backend expects DER format, so convert
    const signatureArray = new Uint8Array(signatureBuffer);
    const derSignature = this.convertP1363ToDER(signatureArray);

    return this.arrayBufferToBase64(derSignature);
  }

  /**
   * Convert IEEE P1363 signature format to DER format
   * Web Crypto API outputs P1363 (r||s), backend expects DER (ASN.1)
   */
  private convertP1363ToDER(p1363Signature: Uint8Array): Uint8Array {
    const r = p1363Signature.slice(0, 32);
    const s = p1363Signature.slice(32, 64);

    const rDer = this.integerToDER(r);
    const sDer = this.integerToDER(s);

    const sequenceLength = rDer.length + sDer.length;

    return new Uint8Array([
      0x30, // SEQUENCE tag
      sequenceLength,
      ...rDer,
      ...sDer,
    ]);
  }

  /**
   * Encode a byte array as DER INTEGER
   */
  private integerToDER(bytes: Uint8Array): Uint8Array {
    // Remove leading zeros but keep at least one byte
    let startIndex = 0;
    while (startIndex < bytes.length - 1 && bytes[startIndex] === 0) {
      startIndex++;
    }
    let trimmed = bytes.slice(startIndex);

    // Add leading zero if high bit is set (to indicate positive number)
    if (trimmed[0] & 0x80) {
      const withZero = new Uint8Array(trimmed.length + 1);
      withZero[0] = 0x00;
      withZero.set(trimmed, 1);
      trimmed = withZero;
    }

    return new Uint8Array([
      0x02, // INTEGER tag
      trimmed.length,
      ...trimmed,
    ]);
  }

  /**
   * Export public key components (x, y coordinates)
   */
  async exportPublicKeyComponents(
    publicKey: CryptoKey,
  ): Promise<{ x: string; y: string }> {
    const jwk = await window.crypto.subtle.exportKey('jwk', publicKey);

    if (!jwk.x || !jwk.y) {
      throw new Error('Failed to extract public key coordinates');
    }

    // JWK uses base64url encoding, convert to standard base64
    return {
      x: this.base64UrlToBase64(jwk.x),
      y: this.base64UrlToBase64(jwk.y),
    };
  }

  /**
   * Convert base64url to standard base64
   */
  private base64UrlToBase64(base64url: string): string {
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    return base64;
  }

  // ============================================
  // Storage Methods using IndexedDB
  // ============================================

  private async saveKeyPairToStorage(keyPair: KeyPairData): Promise<void> {
    if (!this.isIndexedDBAvailable()) {
      console.warn('IndexedDB not available, keys will not be persisted');
      return;
    }

    try {
      const privateKeyJwk = await window.crypto.subtle.exportKey(
        'jwk',
        keyPair.privateKey,
      );
      const publicKeyJwk = await window.crypto.subtle.exportKey(
        'jwk',
        keyPair.publicKey,
      );

      await this.storeInIndexedDB(this.PRIVATE_KEY_ID, privateKeyJwk);
      await this.storeInIndexedDB(this.PUBLIC_KEY_ID, publicKeyJwk);
    } catch (error) {
      console.warn('Failed to save keys to storage:', error);
      // Don't throw - key generation still succeeded
    }
  }

  private async loadKeyPairFromStorage(): Promise<KeyPairData | null> {
    if (!this.isIndexedDBAvailable()) {
      return null;
    }

    try {
      const privateKeyJwk = await this.retrieveFromIndexedDB(
        this.PRIVATE_KEY_ID,
      );
      const publicKeyJwk = await this.retrieveFromIndexedDB(this.PUBLIC_KEY_ID);

      if (!privateKeyJwk || !publicKeyJwk) {
        return null;
      }

      const privateKey = await window.crypto.subtle.importKey(
        'jwk',
        privateKeyJwk,
        this.ALGORITHM,
        true,
        ['sign'],
      );

      const publicKey = await window.crypto.subtle.importKey(
        'jwk',
        publicKeyJwk,
        this.ALGORITHM,
        true,
        ['verify'],
      );

      return { publicKey, privateKey };
    } catch (error) {
      console.warn('Failed to load keys from storage:', error);
      return null;
    }
  }

  async clearKeys(): Promise<void> {
    if (!this.isIndexedDBAvailable()) {
      return;
    }

    try {
      await this.removeFromIndexedDB(this.PRIVATE_KEY_ID);
      await this.removeFromIndexedDB(this.PUBLIC_KEY_ID);
    } catch (error) {
      console.warn('Failed to clear keys:', error);
    }
  }

  // ============================================
  // IndexedDB Helper Methods
  // ============================================

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.KEY_STORE_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys', { keyPath: 'id' });
        }
      };
    });
  }

  private async storeInIndexedDB(key: string, value: JsonWebKey): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      store.put({ id: key, data: value });

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  }

  private async retrieveFromIndexedDB(key: string): Promise<JsonWebKey | null> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');
      const getRequest = store.get(key);

      getRequest.onsuccess = () => {
        db.close();
        resolve(getRequest.result?.data || null);
      };
      getRequest.onerror = () => {
        db.close();
        reject(getRequest.error);
      };
    });
  }

  private async removeFromIndexedDB(key: string): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      store.delete(key);

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  }

  // ============================================
  // Utility Methods
  // ============================================

  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}

